import ApiInstance from './instance'
import {
  ELetterStatus,
  EStatementStatuses,
  IBatch,
  IBatchListFiltersDTO,
  IBatchListResponseDTO,
  ILettersFiltersDTO,
  IMailStatements,
  IStatement,
  IStatementListFilterDTO,
  IStatementListResponseDTO,
  URL_MAPPING
} from '@zunta/configurations'

const { statement: statementUrls, mail: mailUrls } = URL_MAPPING

export default class StatementApi {
  instance: ApiInstance

  constructor (instance: ApiInstance) {
    this.instance = instance
  }

  deleteBatchById (batchId: number | string): Promise<'OK'> {
    return this.instance.doDelete(`${statementUrls.prefix}${statementUrls.deleteBatchById.path}/${batchId}`)
  }

  fetchLettersBatchStatistics (batchId: number | string): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}${statementUrls.fetchLettersBatchStatistics.path}/${batchId}`)
  }

  sendStatementsBatchMails (batchId: number | string): Promise<'OK'> {
    return this.instance.doPost(`${mailUrls.prefix}${mailUrls.sendStatementsBatchMails.path}/${batchId}`)
  }

  getStatementTrackingInfo (statementId: number): Promise<any> {
    return this.instance.doGet(`${mailUrls.prefix}${mailUrls.getStatementTrackingInfo.path}/${statementId}`, {}, 'getStatementTrackingInfo')
  }

  getBatchListByFilters (filters: IBatchListFiltersDTO): Promise<IBatchListResponseDTO> {
    return this.instance.doGet(`${statementUrls.prefix}${statementUrls.batchList.path}`, filters, null, { isCompressed: true })
  }

  getBatchById (batchId: number | string): Promise<IBatch> {
    return this.instance.doGet(`${statementUrls.prefix}${statementUrls.getBatchById.path}/${batchId}`, {}, 'getBatchById')
  }

  confirmMissingPayerCharges (batchId: number): Promise<IBatch> {
    return this.instance.doGet(`${statementUrls.prefix}/confirmMissingPayerCharges/${batchId}`)
  }

  getStatementById (statementId: number | string): Promise<IStatement> {
    return this.instance.doGet(`${statementUrls.prefix}${statementUrls.getStatementById.path}/${statementId}`, {}, 'getStatementById')
  }

  getMailStatements (batchId: number | string): Promise<IMailStatements> {
    return this.instance.doGet(`${mailUrls.prefix}/getStatementBatchDashboard/${batchId}`, {}, 'getStatementById')
  }

  getBatchStatementListByFilters (filters: IStatementListFilterDTO, batchId: string | number): Promise<IStatementListResponseDTO> {
    return this.instance.doGet(`${statementUrls.prefix}${statementUrls.batchStatementList.path}/${batchId}`, filters, null, { isCompressed: true })
  }

  getResidentStatementListByFilters (filters: IStatementListFilterDTO, residentId: string | number): Promise<IStatementListResponseDTO> {
    return this.instance.doGet(`${statementUrls.prefix}${statementUrls.residentStatementList.path}/${residentId}`, filters)
  }

  changeStatementStatus (statementId: number | string, data: { status: EStatementStatuses, batch: number }): Promise<'OK'> {
    return this.instance.doPut(`${statementUrls.prefix}${statementUrls.changeStatus.path}/${statementId}`, data)
  }

  changeLetterStatus (letterId: number | string, status: ELetterStatus): Promise<'OK'> {
    return this.instance.doPut(`${statementUrls.prefix}/letterStatus/${letterId}`, { status })
  }

  updateStatementsByIds (statementsIds: (number | string)[], data: Partial<IStatement>, batchId: number): Promise<'OK'> {
    return this.instance.doPut(`${statementUrls.prefix}/statements`, { statementsIds, data, batchId })
  }

  fetchFacilitiesStatements (facilities: number[]): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}${statementUrls.fetchFacilitiesStatements.path}`, {
      facilities
    })
  }

  fetchFacilitiesNCSStatements (facilities: number[]): Promise<any> {
    return this.instance.doPost(`/ncs/fetchFacilitiesNCSStatements`, {
      facilities
    })
  }

  fetchBatchStatements (batchId: number | string, data: { isUpdating: boolean }): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}${statementUrls.fetchBatchStatements.path}/${batchId}`, data)
  }

  fetchBatchNCSStatements (batchId: number | string, data: { isUpdating: boolean }): Promise<any> {
    return this.instance.doPost(`/ncs/fetchBatchNCSStatements/${batchId}`, data)
  }

  addStatementMessage (statementId: number | string, data: { title: string, body: string }): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}${statementUrls.addStatementMessage.path}/${statementId}`, data)
  }

  addStatementLetter (statementId: number | string, data: any): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}/addTemplateToStatement/${statementId}`, data)
  }

  getStatementMessagesV2 (residentId: number | string, batchId: number | string): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}/getStatementMessagesV2/${residentId}/${batchId}`)
  }

  addStatementMessageLetterV2 (data: any): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}/addStatementMessageV2`, data)
  }

  updateStatementMessageLetterV2 (data: any): Promise<any> {
    return this.instance.doPut(`${statementUrls.prefix}/changeStatementMessageActiveStateV2`, data)
  }

  regenerateStatement (statementId: number | string): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}${statementUrls.regenerateStatement.path}/${statementId}`)
  }

  regenerateLetter (statementId: number | string): Promise<any> {
    return this.instance.doPost(`${statementUrls.prefix}${statementUrls.regenerateLetter.path}/${statementId}`)
  }

  changeStatementMessageActiveState (statementMessageId: number | string, active: boolean): Promise<any> {
    return this.instance.doPut(`${statementUrls.prefix}${statementUrls.changeStatementMessageActiveState.path}/${statementMessageId}`, { active })
  }

  fetchReturnedBatchItemsListByFilters (batchId: string | number, filters: IStatementListFilterDTO): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}/getReturnedBatchItems/${batchId}`, filters)
  }

  fetchReturnedBatchStatistics (batchId: string | number): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}/getReturnedBatchStatistic/${batchId}`)
  }

  sendLettersBatchMails (batchId: number | string): Promise<any> {
    return this.instance.doPost(`${mailUrls.prefix}/sendLettersBatchMails/${batchId}`)
  }

  importNcsStatements (facilityId: number | string, privateFile: any, pendingFile: any): Promise<any> {
    const form = new FormData()
    form.append('privateFile', privateFile)
    form.append('pendingFile', pendingFile)
    return this.instance.doPost(`/ncs/generateStatements/${facilityId}`, form)
  }

  updateStatements (facilityId: number | string, batchId: number | string, privateFile: any, pendingFile: any): Promise<any> {
    const form = new FormData()
    form.append('privateFile', privateFile)
    form.append('pendingFile', pendingFile)
    return this.instance.doPost(`/ncs/updateStatements/${facilityId}`, form, {
      params: {
        batchId
      }
    })
  }

  getResidentBatchStatements (residentId: string | number, batchId: string | number): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}/residentBatchStatements/${residentId}/${batchId}`)
  }

  getStatementLineItems (statementId: string | number, includePreviousBreakdown: boolean = false): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}/statementLineItems/${statementId}`, { includePreviousBreakdown })
  }

  updateStatementLineItems (statementId: string | number, payload: any): Promise<any> {
    return this.instance.doPut(`${statementUrls.prefix}/statementLineItems/${statementId}`, { ...payload })
  }

  revertStatement (statementId: string | number, forAll: boolean): Promise<any> {
    return this.instance.doPut(`${statementUrls.prefix}/revertStatement/${statementId}`, { forAll })
  }

  async fetchStatementsZip (statementReferenceId: { id: number, type: 'resident' | 'batch' }, filtersDTO: IStatementListFilterDTO | ILettersFiltersDTO): Promise<{
    message: string;
    hasCorruptFiles: boolean;
    status: number
  }> {
    return this.instance.downloadFilesFromUrl({
      statement: {
        urlStr: `${statementUrls.prefix}/statementsZip/${statementReferenceId.id}`,
        statementReferenceId,
        filtersDTO
      }
    })
  }

  fetchBatchStatementNoCharge (batchId: number | string): Promise<any> {
    return this.instance.doGet(`${statementUrls.prefix}/statementsNoChargeCount/${batchId}`)
  }

  getPaymentsNotSync (batchId: number) {
    return this.instance.doGet(`${statementUrls.prefix}/paymentsNotSync/${batchId}`)
  }

  getStatementBreakdownItems (statementId: number | string) {
    return this.instance.doGet(`${statementUrls.prefix}/statementBreakdownItems/${statementId}`)
  }

  async getNewChargesIsGenerated (batchId: number | string) {
    return this.instance.doGet(`${statementUrls.prefix}/checkNewChargesIsGenerated/${batchId}`)
  }

  batchHasPendingActivities (batchId: number) {
    return this.instance.doGet(`/activities/batchHasPendingActivities/${batchId}`)
  }

  replaceBatchUser (facilityId: number, userId: number, payload: any) {
    return this.instance.doPut(`${statementUrls.prefix}/replaceBatchUser/${facilityId}/${userId}`, payload)
  }

  getFacilitiesPaymentsNotInSync (facilities: any[]) {
    return this.instance.doPost(`${statementUrls.prefix}/paymentsNotSync`, { facilities })
  }

  confirmNotPostedPopup (batchId: number) {
    return this.instance.doPut(`${statementUrls.prefix}/confirmNotPostedPopup/${batchId}`)
  }
}
